import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import './About.css'; 
import '../pages/Services/Services.css';
import SEO from '../components/SEO/SEO';

const About = () => {
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const location = useLocation();

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
    });

    // Scroll to top handler
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Handle hash navigation
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          const offset = 150; // Account for fixed header + spacing
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }, 100);
      }
    }
  }, [location]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <SEO 
        title="Who We Are"
        description="Learn about Sradha Technologies - a team of passionate innovators dedicated to transforming businesses through technology solutions and digital innovation."
        keywords="about us, tech company, innovation, digital solutions, IT services, technology team"
        canonical="https://sradhatech.com/about"
      />
      <div className="about-page">
        <div className="service-header">
          <h1>Who We Are</h1>
          <p>A team of passionate innovators shaping the future of technology</p>
        </div>

        <div className="container">
          <div id="story" className="about-content">
            <div className="about-text">
              <h2>Our Story</h2>
              <p>
                At Sradha Technologies, we&apos;re more than just a tech company. We&apos;re a
                collective of forward-thinking minds dedicated to transforming ideas
                into impactful digital solutions.
              </p>
              <p>
                Since our inception, we&apos;ve been driven by a simple yet powerful
                mission: to make technology work smarter for businesses and create
                experiences that matter.
              </p>
            </div>
            <div className="about-map">
              {isMapLoading && (
                <div className="map-loading">
                  <div className="spinner"></div>
                </div>
              )}
              <iframe
                src="https://maps.google.com/maps?q=6860%20Dallas%20Pkwy%20%23200%20Plano%2C%20TX%2075024&z=15&output=embed"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Sradha Technologies Location"
                onLoad={() => setIsMapLoading(false)}
                className={isMapLoading ? 'loading' : ''}
              ></iframe>
              <div className="map-actions">
                <a 
                  href="https://www.google.com/maps/dir/?api=1&destination=6860+Dallas+Pkwy+%23200,+Plano,+TX+75024"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-directions"
                >
                  <i className="fas fa-directions"></i>
                  Get Directions
                </a>
              </div>
            </div>
          </div>

          {/* Section Divider */}
          <div className="section-divider"></div>

          {/* Partnership Section */}
          <section id="almeone-partnership" className="partnership-section" data-aos="fade-up">
            <div className="section-header">
              <h2>Our Strategic Partnerships</h2>
              <p>Collaborating with industry leaders to deliver exceptional value</p>
            </div>
            
            <div className="partnership-content">
              <div className="partnership-card" data-aos="fade-up" data-aos-delay="100">
                <div className="partnership-card-header">
                  <div className="partnership-icon">
                    <i className="fas fa-handshake"></i>
                  </div>
                  <h3>AlmeOne</h3>
                </div>
                <div className="partnership-card-body">
                  <p>
                    We are proud to partner with <a href="https://www.almeone.com/" target="_blank" rel="noopener noreferrer" style={{color: 'var(--primary-blue)', fontWeight: '600', textDecoration: 'none', borderBottom: '2px solid var(--primary-blue)'}}>AlmeOne</a>, a leading innovator in digital solutions. 
                    Together, we combine our expertise to deliver cutting-edge technology services 
                    that drive business transformation and create lasting value for our clients.
                  </p>
                  <ul className="partnership-benefits">
                    <li><i className="fas fa-cogs"></i> Enhanced service capabilities</li>
                    <li><i className="fas fa-graduation-cap"></i> Expanded technological expertise</li>
                    <li><i className="fas fa-globe"></i> Global reach and resources</li>
                    <li><i className="fas fa-lightbulb"></i> Innovation-driven solutions</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Section Divider */}
          <div className="section-divider"></div>

          {/* Microsoft Cloud Services Section */}
          <section id="microsoft-partnership" className="partnership-section" data-aos="fade-up">
            <div className="section-header">
              <h2>Microsoft Cloud Services</h2>
              <p>Leveraging Microsoft Azure to deliver enterprise cloud solutions</p>
            </div>
            
            <div className="partnership-content">
              <div className="partnership-card" data-aos="fade-up" data-aos-delay="100">
                <div className="partnership-card-header">
                  <div className="partnership-icon">
                    <i className="fab fa-microsoft"></i>
                  </div>
                  <h3>Microsoft Partner</h3>
                </div>
                <div className="partnership-card-body">
                  <p>
                    We partner with Microsoft to deliver cutting-edge cloud solutions to our clients. 
                    Our expertise in Microsoft Azure cloud services enables us to provide scalable, 
                    secure, and reliable cloud infrastructure that powers modern business applications 
                    and accelerates digital transformation.
                  </p>
                  <ul className="partnership-benefits">
                    <li><i className="fas fa-cloud"></i> Azure Cloud Infrastructure</li>
                    <li><i className="fas fa-sync-alt"></i> Cloud Migration Services</li>
                    <li><i className="fas fa-expand-arrows-alt"></i> Scalable Solutions</li>
                    <li><i className="fas fa-shield-alt"></i> Enterprise-Grade Security</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Section Divider */}
          <div className="section-divider"></div>

          {/* Dynatrace Partnership Section */}
          <section id="dynatrace-partnership" className="partnership-section" data-aos="fade-up">
            <div className="section-header">
              <h2>Observability & Monitoring Partnership</h2>
              <p>Full-stack observability and AI-powered performance monitoring solutions</p>
            </div>
            
            <div className="partnership-content">
              <div className="partnership-card" data-aos="fade-up" data-aos-delay="100">
                <div className="partnership-card-header">
                  <div className="partnership-icon">
                    <i className="fas fa-chart-line"></i>
                  </div>
                  <h3>Dynatrace Partner</h3>
                </div>
                <div className="partnership-card-body">
                  <p>
                    We partner with Dynatrace to deliver enterprise-grade observability and performance monitoring solutions. 
                    Our expertise in Dynatrace&apos;s AI-powered platform enables us to provide comprehensive full-stack observability, 
                    automated root cause analysis, and real-time performance insights that help organizations optimize their digital 
                    infrastructure and ensure exceptional user experiences.
                  </p>
                  <ul className="partnership-benefits">
                    <li><i className="fas fa-tachometer-alt"></i> Full-Stack Application Performance Monitoring (APM)</li>
                    <li><i className="fas fa-brain"></i> AI-Powered Root Cause Analysis</li>
                    <li><i className="fas fa-network-wired"></i> Infrastructure, Logs, Traces, and Metrics Correlation</li>
                    <li><i className="fas fa-bell"></i> Real-Time Alerting and Anomaly Detection</li>
                    <li><i className="fas fa-chart-bar"></i> SLA, SLO, and Error Budget Monitoring</li>
                    <li><i className="fas fa-dollar-sign"></i> Cost and Performance Optimization Insights</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Section Divider */}
          <div className="section-divider"></div>
 
          {/* CTA Section */}
          <section className="partnership-section" data-aos="fade-up">
            <div className="section-header">
              <h2>Let&apos;s Build Something Amazing Together</h2>
              <p>Partner with us to transform your business with innovative technology solutions</p>
            </div>
            
            <div className="partnership-content">
              <div className="partnership-card" data-aos="fade-up" data-aos-delay="100">
                <div className="partnership-card-header">
                  <div className="partnership-icon">
                    <i className="fas fa-handshake"></i>
                  </div>
                  <h3>Ready to Work Together</h3>
                </div>
                <div className="partnership-card-body">
                  <p>
                    Our team is ready to help you achieve your goals. Contact us today to start your 
                    digital transformation journey with cutting-edge technology solutions tailored to 
                    your business needs.
                  </p>
                  <ul className="partnership-benefits">
                    <li><i className="fas fa-users"></i> Expert Team</li>
                    <li><i className="fas fa-headset"></i> 24/7 Support</li>
                    <li><i className="fas fa-rocket"></i> Fast Delivery</li>
                    <li><i className="fas fa-trophy"></i> Proven Results</li>
                  </ul>
                  <div className="partnership-buttons">
                    <a href="/contact" className="partnership-btn">
                      <i className="fas fa-paper-plane"></i>
                      Start Your Project
                    </a>
                    <a href="/services" className="partnership-btn">
                      <i className="fas fa-th-large"></i>
                      Explore Services
                    </a>
                  </div>
                  <p style={{marginTop: '2rem', fontSize: '1.1rem', fontWeight: '600', color: 'var(--primary-blue)'}}>
                    <i className="fas fa-phone-alt"></i>
                    {' '}Call us: <a href="tel:+19452497292" style={{color: 'var(--primary-blue)', textDecoration: 'none'}}>+1 (945) 249-7292</a>
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Scroll to Top Button */}
        {showScrollTop && (
          <button 
            className="scroll-to-top"
            onClick={scrollToTop}
            aria-label="Scroll to top"
          >
            <i className="fas fa-arrow-up"></i>
          </button>
        )}
      </div>
    </>
  );
};

export default About;
